import Head from 'next/head'
import Link from 'next/link'
import { ReactNode } from 'react'

// 赛制说明中心:公开、可分享、可扩展。以后各种赛制/移位制的差异都放这里。
// 每个赛制 = 对比表一行 + 一个带锚点的详解小节(链接 /formats#<id> 可直达)。

type Format = {
  id: string
  name: string
  badge: string
  oneLiner: string
  winners: string
  requirement: string
  rounds: string
  scene: string
  body: ReactNode
}

const Section = ({ children, title }: { title: string; children: ReactNode }) => (
  <div className="mb-4">
    <div className="mb-1.5 text-sm font-bold text-gray-800">{title}</div>
    <div className="text-[15px] leading-7 text-gray-600">{children}</div>
  </div>
)

const FORMATS: Format[] = [
  {
    id: 'guandan',
    name: '掼蛋编排（瑞士制）',
    badge: '动态配对',
    oneLiner: '按成绩滚动配对、凭真实战绩争排名，掼蛋赛的主流编排。',
    winners: '1 个（总排名第一）',
    requirement: '队伍数灵活，双人赛 / 团体赛 / 单人赛',
    rounds: '自定义轮数',
    scene: '掼蛋比赛，凭真实战绩排名次',
    body: (
      <>
        <Section title="一句话">
          每轮打完按当前排名<strong className="text-gray-900">动态配对</strong>：让名次相近的队伍两两对阵、且尽量不重复碰面（瑞士制思路）。谁赢得多、赢得漂亮，谁排得高，全场评出唯一总冠军。
        </Section>
        <Section title="配对三原则（瑞士赛）">
          <strong>① 成绩相近的对阵</strong>——每轮按当前排名，把名次相近的队伍两两配对；
          <strong>② 对阵过一次的不再对阵</strong>——避免重复碰面；
          <strong>③ 同俱乐部的永不对阵</strong>——同一俱乐部/工会内部尽量不互打。
          <br />
          本系统在此之上还叠加：种子不碰种子、轮空自动处理。这样少数几轮就能把真实实力区分开，不必所有队两两打满。
        </Section>
        <Section title="首轮怎么起（尚无成绩）">
          第 1 轮还没有成绩，可用四种方式起始：<strong>相邻配对</strong>（1 对 2、3 对 4……）、
          <strong>首尾配对</strong>（1 对 100、2 对 99……）、<strong>拦腰配对</strong>（1 对 51、2 对 52……）、
          <strong>随机配对</strong>。此后每轮按最新成绩重排，再套用上面的三原则。
        </Section>
        <Section title="排名怎么算（按优先级）">
          依次比较：<strong className="text-gray-900">胜负分 → 胜场数 → 场分 → 级差分 → 对手积分</strong>。
          <br />
          胜负分 = 每局 胜 2 / 平 1 / 负 0；场分 = 每局本方原始得分累加；级差分 = 每局（名次 − 2）累加，反映赢得是否彻底；对手积分（Buchholz）= 你所有对手的场分之和，同分时对手更硬的排在前面。各词详见
          <a href="/baike" className="underline">灵缇百科</a>。
        </Section>
        <Section title="支持的赛制">
          <strong>双人赛</strong>（2 人一队）、<strong>团体赛</strong>（4 人一队）、<strong>单人赛</strong>等，编排与计分逻辑一致，仅参赛单位不同。
        </Section>
        <Section title="和复式（米切尔 / 豪厄尔）的区别">
          掼蛋编排是<em>「按成绩动态配对、比谁赢得多」</em>，牌是现发的、各桌不同；复式移位是<em>「固定移位、大家打同一副牌、比同牌下谁打得好」</em>，用来消除牌运。两条线各有适用场景。
        </Section>
      </>
    ),
  },
  {
    id: 'mitchell',
    name: '米切尔移位制',
    badge: '双冠军',
    oneLiner: '南北线、东西线各出一个冠军的复式移位。',
    winners: '2 个（南北线 + 东西线）',
    requirement: '桌数 ≥ 3，奇偶桌均可',
    rounds: '单数桌 n 轮（完全）/ 双数桌 n−1 轮（不完全）',
    scene: '人多、要快速出成绩，两条线各自排名',
    body: (
      <>
        <Section title="一句话">
          n 张桌、2n 对同时开赛，南北方向坐着不动、东西方向每轮换桌，同一副牌在多桌重复打，比谁打得好。南北线和东西线各自独立排名，因此产生
          <strong className="text-gray-900"> 两个冠军</strong>——这就是「双冠军」的由来。
        </Section>
        <Section title="布局">
          n 桌，每桌 4 人 = 2 对，共 2n 对。<strong>南北（NS）的 n 对固定不动</strong>；
          <strong>东西（EW）的 n 对逐轮上移一桌</strong>；牌集（board）在各桌之间 relay 传递。
        </Section>
        <Section title="单数桌：完全米切尔（n 轮）">
          桌数为<strong className="text-gray-900">单数</strong>时一路顺移即可跑满 <strong className="text-gray-900">n 轮</strong>：每个东西对恰好遇齐全部南北对、每对打齐全部 n 副牌且不重复。这叫
          <strong className="text-gray-900">完全米切尔</strong>。
        </Section>
        <Section title="双数桌：不完全米切尔（n−1 轮）">
          桌数为<strong className="text-gray-900">双数</strong>时如果也跑满 n 轮就会
          <strong className="text-gray-900">撞车</strong>（同一对重复打到同一副牌）。所以必须
          <strong className="text-gray-900">减掉一轮、只打 n−1 轮</strong>，并在打到一半（第 n/2 轮起）东西对多跳一桌（跳桌），破除奇偶。这叫
          <strong className="text-gray-900">不完全米切尔</strong>，保证
          <em>任意两对至多相遇一次、每对不重复打同一副、每副至少两桌打过可横比</em>。
        </Section>
        <Section title="6 桌实例（不完全米切尔）">
          6 桌是<strong className="text-gray-900">双数</strong>，所以是不完全米切尔：6 桌 = 12 对 →
          <strong className="text-gray-900"> 打 5 轮</strong>（n−1）。东西对每轮上移一桌，<strong className="text-gray-900">第 3 轮起多跳一桌</strong>；跑完 5 轮，每对打齐 5 副不同的牌、彼此不重复相遇（不减这一轮就会撞车）。系统会对生成的排台自动校验，通过才显示「排台校验通过」。
        </Section>
        <Section title="计分：Matchpoint 配对分">
          同一副牌在各桌的成绩<strong>横向两两比较</strong>：胜 2、平 1、负 0；每副满分 =（打此副的桌数 − 1）× 2，汇总成百分比。拱猪/华牌这类「分低者胜」的项目，勾选后自动反向比。
        </Section>
        <Section title="出双冠军">
          南北线、东西线互不直接对比，各自按 Matchpoint 百分比排名，分别产生南北线冠军与东西线冠军。
        </Section>
      </>
    ),
  },
  {
    id: 'howell',
    name: '豪厄尔移位制',
    badge: '单冠军',
    oneLiner: '全场只排一个名次的复式移位，适合桌数少、要争唯一冠军。',
    winners: '1 个（全场统一）',
    requirement: '对数为偶数且 ≥ 4（对数 / 2 = 桌数）',
    rounds: '最多 m−1 轮（m = 对数）',
    scene: '桌数少、要评出唯一总冠军',
    body: (
      <>
        <Section title="一句话">
          所有对子在一张统一的名次表里比拼，最终只有<strong className="text-gray-900">一个总冠军</strong>。适合桌数较少、希望评出唯一第一名的场合。
        </Section>
        <Section title="怎么排">
          m 对（偶数）、m/2 桌。用圆桌轮转法：固定一号位、其余绕圈，保证任意两对在整个赛程中恰好相遇一次。每轮各桌发同一副新牌，用 Matchpoint 横比。
        </Section>
        <Section title="计分与米切尔一致">
          同样是 Matchpoint 配对分，只是所有对子并入同一名次表——所以是单冠军，而非米切尔的南北/东西两条线。
        </Section>
      </>
    ),
  },
]

export default function FormatsHub() {
  return (
    <>
      <Head>
        <title>赛制说明 · 米切尔 / 豪厄尔移位制怎么运作 | 灵缇</title>
        <meta
          name="description"
          content="灵缇赛制说明中心:米切尔双冠军移位制(单数桌=完全米切尔 n 轮,双数桌=不完全米切尔 n−1 轮)、豪厄尔单冠军移位制的排台与计分方式，以及各类赛制的区别。"
        />
      </Head>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 py-10 md:py-14">
          <header className="mb-10 text-center">
            <div className="mb-4 text-left">
              <Link href="/rules" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                ← 返回灵缇规则库
              </Link>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm ring-1 ring-gray-200">
              📖 规则库 · 赛制说明
            </span>
            <h1 className="mt-4 text-4xl font-black tracking-tight text-gray-900 md:text-5xl">赛制说明</h1>
            <p className="mt-3 text-gray-500">各类移位制 / 赛制的运作方式与区别，一页看懂。</p>
          </header>

          {/* 对比表 */}
          <section className="mb-12">
            <h2 className="mb-4 text-lg font-bold text-gray-800">赛制对比</h2>
            <div className="overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400">
                    <th className="px-4 py-3 font-medium">赛制</th>
                    <th className="px-4 py-3 font-medium">冠军数</th>
                    <th className="px-4 py-3 font-medium">桌/对数要求</th>
                    <th className="px-4 py-3 font-medium">轮数</th>
                    <th className="px-4 py-3 font-medium">适用场景</th>
                  </tr>
                </thead>
                <tbody>
                  {FORMATS.map((f) => (
                    <tr key={f.id} className="border-b border-gray-50 last:border-0 align-top">
                      <td className="px-4 py-3">
                        <a href={`#${f.id}`} className="font-semibold text-gray-900 hover:underline">
                          {f.name}
                        </a>
                        <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{f.badge}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{f.winners}</td>
                      <td className="px-4 py-3 text-gray-600">{f.requirement}</td>
                      <td className="px-4 py-3 text-gray-600">{f.rounds}</td>
                      <td className="px-4 py-3 text-gray-600">{f.scene}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 详解小节 */}
          {FORMATS.map((f) => (
            <section key={f.id} id={f.id} className="mb-10 scroll-mt-6">
              <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 md:p-8">
                <div className="mb-4 flex items-center gap-2">
                  <h2 className="text-2xl font-black text-gray-900">{f.name}</h2>
                  <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">{f.badge}</span>
                </div>
                <p className="mb-6 text-[15px] italic leading-7 text-gray-400">{f.oneLiner}</p>
                {f.body}
              </div>
            </section>
          ))}

          <p className="mt-6 text-center text-sm text-gray-400">更多赛制（循环赛、淘汰赛、积分赛等）陆续补充。</p>
        </div>
      </div>
    </>
  )
}
